/**
 * LocationMapSection — the inline map below the BOM tables on estimate results.
 * Shared by EstimateResults (wizard) and SharedEstimate, like CustomTablesSection.
 *
 * Renders nothing unless the loaded retailer context resolved an inline map
 * (products.php → locationMaps.inlineMapId). Never printed (d-print-none) and never in the
 * PDF (pdf.ts draws only what it knows about). The map library loads lazily, and only once
 * the section scrolls near the viewport.
 */

import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { getInlineMapId } from '../../data/productTable';

const LocationMap = lazy(() => import('./LocationMap'));

interface Props {
  placement: 'belowBom';
  estimateGuid?: string | null;
  retailerId?: number | null;
}

export const LocationMapSection: React.FC<Props> = ({ estimateGuid, retailerId }) => {
  const mapId = getInlineMapId();
  const hostRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!mapId || visible) return;
    const el = hostRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { rootMargin: '250px' });
    io.observe(el);
    return () => io.disconnect();
  }, [mapId, visible]);

  if (!mapId) return null;
  return (
    <section ref={hostRef} className="location-map-section d-print-none print-hide" style={{ minHeight: visible ? undefined : 240 }}>
      {visible && (
        <Suspense fallback={<div style={{ minHeight: 240 }} />}>
          <LocationMap mapId={mapId} context="inline" estimateGuid={estimateGuid ?? null} retailerId={retailerId ?? null} />
        </Suspense>
      )}
    </section>
  );
};

export default LocationMapSection;
